import { computed } from 'vue'

import { useChatQueries } from '@/apiClient/queries/useChatQueries'
import type { IWebsiteSummary } from '@/apiClient/types/websites'
import { useChatStore } from '@/store/chat'
import { usePreviewStore } from '@/store/preview'
import { htmlBlocksInMessage } from '@/utils/chatHtmlBlocks'
import {
  resolveHtmlPreviewPublishFromMessage,
  websiteMetaForHtmlBlock,
} from '@/utils/publishing'
import { extractHtmlPageTitle } from '@/utils/websiteSlug'

/**
 * `extractHtmlPageTitle` parses with `DOMParser`; `chatHtmlBlocks` recomputes on
 * every streamed token, so memoize titles by raw HTML to keep stable blocks cheap.
 * Only the block still streaming produces fresh (short-lived) entries.
 */
const htmlTitleCache = new Map<string, string>()
function cachedHtmlPageTitle(raw: string): string {
  const cached = htmlTitleCache.get(raw)
  if (cached !== undefined) return cached
  // Bound growth from a long streaming session (partial-HTML keys accumulate).
  if (htmlTitleCache.size > 500) htmlTitleCache.clear()
  const title = extractHtmlPageTitle(raw)
  htmlTitleCache.set(raw, title)
  return title
}

/** One generated HTML block in the chat, for the preview HTML selector. */
export interface ChatHtmlPreviewBlock {
  chatId: string
  messageId: string
  blockIndex: number
  /** Page title (from `<title>`/`<h1>`), falls back to "My webpage". */
  title: string
  /**
   * The page's HTML. Present for pages in loaded messages (rendered immediately);
   * `undefined` for descriptor-only pages whose message isn't loaded — the selector
   * fetches it on demand (see `onSelectHtmlBlock`).
   */
  raw?: string
  /** Linked website (from `file.metadata.website_id`), if published/linked. */
  websiteId?: string
  isPublished: boolean
  /** When published: the page's publish date (unix seconds). From the backend
   * descriptor (EVGPT-1565), or approximated by the toolbar; `undefined` when unknown. */
  publishedAt?: number
  /** Locator for on-demand message loading (unix seconds); set for descriptor-only pages. */
  messageTimestamp?: number
}

/** Resolved publish context for the open HTML preview panel. */
export function useHtmlPreviewPublish() {
  const previewStore = usePreviewStore()
  const chatStore = useChatStore()

  const htmlContent = computed(() => previewStore.htmlContent)

  const resolvedHtmlPublish = computed(() => {
    const c = previewStore.content
    if (!c || c.type !== 'html' || !c.htmlPublishKey) {
      return {
        publishPath: undefined as string | undefined,
        publishedHtmlApiUrl: undefined as string | undefined,
      }
    }
    const key = c.htmlPublishKey
    const message = chatStore.getChatMessage(key.chatId, key.messageId)
    return resolveHtmlPreviewPublishFromMessage(message, key)
  })

  const publishPath = computed<string | null>(() => {
    const path = resolvedHtmlPublish.value.publishPath
    return path ? path : null
  })

  const publishedHtmlApiUrl = computed<string | null>(() => {
    const c = previewStore.content
    if (!c || c.type !== 'html') return null
    if (c.publishedHtmlApiUrl) return c.publishedHtmlApiUrl
    return resolvedHtmlPublish.value.publishedHtmlApiUrl ?? null
  })

  const websiteId = computed(() => {
    const c = previewStore.content
    return c?.type === 'html' ? (c.websiteId ?? null) : null
  })

  const websiteSlug = computed({
    get: () => {
      const c = previewStore.content
      return c?.type === 'html' ? (c.websiteSlug ?? '') : ''
    },
    set: (slug: string) => {
      previewStore.updateHtmlPreview({ websiteSlug: slug })
    },
  })

  const websiteName = computed({
    get: () => {
      const c = previewStore.content
      return c?.type === 'html' ? (c.websiteName ?? '') : ''
    },
    set: (name: string) => {
      previewStore.updateHtmlPreview({ websiteName: name })
    },
  })

  const websiteDescription = computed({
    get: () => {
      const c = previewStore.content
      return c?.type === 'html' ? (c.websiteDescription ?? '') : ''
    },
    set: (description: string) => {
      previewStore.updateHtmlPreview({ websiteDescription: description })
    },
  })

  const websiteOwnedSlug = computed(() => {
    const c = previewStore.content
    return c?.type === 'html' ? (c.websiteOwnedSlug ?? null) : null
  })

  const isWebsitePublished = computed(() => {
    const c = previewStore.content
    if (c?.type !== 'html') return false
    // When the block is linked to a website, its `file.metadata` publish state
    // (EVGPT-1532) is authoritative: after unpublish the published file may linger
    // but must not read as published.
    const key = c.htmlPublishKey
    if (key) {
      const message = chatStore.getChatMessage(key.chatId, key.messageId)
      const meta = websiteMetaForHtmlBlock(message?.files, key.blockIndex)
      if (meta) return meta.isPublished
    }
    if (c.isWebsitePublished) return true
    return Boolean(c.publishedHtmlApiUrl ?? publishedHtmlApiUrl.value)
  })

  /**
   * The chat the open HTML preview belongs to. Prefer the previewed block's
   * publish key, but fall back to the active chat so the selector and per-chat
   * website resolution still work when the preview was opened without a publish
   * key (e.g. the streaming auto-preview, or an unlinked block).
   */
  const previewChat = computed(() => {
    const c = previewStore.content
    const keyChatId = c?.type === 'html' ? c.htmlPublishKey?.chatId : undefined
    const byKey = keyChatId ? chatStore.getChatById(keyChatId) : undefined
    return byKey ?? chatStore.activeChat ?? null
  })

  /**
   * The complete, document-ordered list of the chat's generated HTML pages
   * (EVGPT-1565), independent of message pagination — includes pages in messages
   * that haven't been loaded yet and generated-but-unpublished pages. Descriptors
   * carry no raw HTML. Fetched only while an HTML preview is open; merged with the
   * loaded-message scan below (which supplies `raw` and the freshest publish state).
   */
  const previewChatId = computed(() =>
    previewStore.content?.type === 'html' ? previewChat.value?.id : undefined,
  )
  const chatQueries = useChatQueries()
  const { data: htmlPageDescriptors } = chatQueries.htmlPages(previewChatId)

  /**
   * Every generated HTML page in the chat, in document order, with its title and
   * linked-website state — populates the preview HTML selector so the user can
   * switch which generated HTML is shown. Combines two sources:
   *   1. a scan of the messages the store holds — the loaded window plus any message
   *      fetched on demand by `ensureMessageLoaded` (carries `raw` for instant render
   *      and the freshest publish state; also covers still-streaming replies), and
   *   2. the backend descriptor list (the authoritative complete/ordered set,
   *      including pages in messages outside the loaded window).
   * Merge is per-message: held messages use the scan; the rest come from descriptors
   * (older than every held message, so they precede them in order).
   */
  const chatHtmlBlocks = computed<ChatHtmlPreviewBlock[]>(() => {
    const chat = previewChat.value
    const id = chat?.id
    if (!id) return []

    const scannedBlocks: ChatHtmlPreviewBlock[] = []
    const scannedMessageIds = new Set<string>()
    for (const message of chatStore.getChatMessagesInStore(id)) {
      if (message.role !== 'assistant' || !message.id) continue
      const messageId = message.id
      scannedMessageIds.add(messageId)

      for (const fence of htmlBlocksInMessage(message)) {
        const meta = websiteMetaForHtmlBlock(message.files, fence.blockIndex)
        scannedBlocks.push({
          chatId: id,
          messageId,
          blockIndex: fence.blockIndex,
          title: cachedHtmlPageTitle(fence.raw),
          raw: fence.raw,
          websiteId: meta?.websiteId,
          isPublished: meta?.isPublished ?? false,
        })
      }
    }

    const descriptors = htmlPageDescriptors.value
    // Endpoint not loaded yet (or empty): fall back to the local scan so the
    // selector still works while the request is in flight / offline.
    if (!descriptors?.length) return scannedBlocks

    // Published date only exists server-side; overlay it onto the scanned blocks
    // (their local `file.metadata` doesn't carry it).
    const publishedAtByKey = new Map<string, number>()
    for (const d of descriptors) {
      if (d.published_at != null) {
        publishedAtByKey.set(`${d.message_id}:${d.block_index}`, d.published_at)
      }
    }
    const enrichedScanned = scannedBlocks.map((block) => {
      const at = publishedAtByKey.get(`${block.messageId}:${block.blockIndex}`)
      return at != null ? { ...block, publishedAt: at } : block
    })

    // Pages whose message the store doesn't hold — older than every scanned message
    // (loading is contiguous from the newest end), so they precede the scanned blocks
    // in document order. No `raw`: the message is fetched when the page is selected.
    const unscannedBlocks: ChatHtmlPreviewBlock[] = []
    for (const d of descriptors) {
      if (scannedMessageIds.has(d.message_id)) continue
      unscannedBlocks.push({
        chatId: id,
        messageId: d.message_id,
        blockIndex: d.block_index,
        title: d.title || 'Untitled page',
        websiteId: d.website_id ?? undefined,
        isPublished: d.is_published,
        publishedAt: d.published_at ?? undefined,
        messageTimestamp: d.message_timestamp,
      })
    }

    return [...unscannedBlocks, ...enrichedScanned]
  })

  /**
   * The chat's single website (one website per chat), straight from
   * GET `/chats/{id}/website` — `null` when the chat has none. Authoritative:
   * it answers "what is this chat's website" server-side and reflects a publish
   * or unpublish immediately, so the resolved website can't desync from the
   * chat's block metadata the way the derivation below could (EVGPT-1511).
   */
  const { data: chatWebsiteData } = chatQueries.website(previewChatId)

  /** The chat website's full row (name, slug, link, `published_at`), if any. */
  const chatWebsiteSummary = computed<IWebsiteSummary | null>(
    () => chatWebsiteData.value ?? null,
  )

  /**
   * Best-effort stand-in used only until the endpoint answers (request in flight,
   * query disabled, or the request failed): the first generated block carrying a
   * `file.metadata.website_id`. After a publish the backend supersedes prior source
   * links, so at most one block is linked at a time. Keeps the toolbar/modal from
   * flashing an "unpublished" state on open, and keeps them working offline.
   */
  const scannedChatWebsite = computed<{
    websiteId: string
    isPublished: boolean
  } | null>(() => {
    for (const block of chatHtmlBlocks.value) {
      if (block.websiteId) {
        return { websiteId: block.websiteId, isPublished: block.isPublished }
      }
    }
    return null
  })

  /** The website every publish in this chat reuses — including one that is linked
   * but not currently published, so re-publishing keeps the same slug. */
  const chatWebsite = computed<{
    websiteId: string
    isPublished: boolean
  } | null>(() => {
    const website = chatWebsiteData.value
    if (website) {
      return { websiteId: website.id, isPublished: website.is_published }
    }
    // `null` is the endpoint's answer ("this chat has no website") and wins over any
    // stale block link; `undefined` means it hasn't answered yet — fall back then.
    if (website === null) return null
    return scannedChatWebsite.value
  })

  /**
   * Website that will be created/updated when the user publishes. Resolves to
   * the chat's website (one slug per chat), so publishing any block reuses it;
   * falls back to the content's seeded `websiteId` before chat metadata loads.
   */
  const effectiveWebsiteId = computed<string | null>(() => {
    return chatWebsite.value?.websiteId ?? websiteId.value ?? null
  })

  /** True when the currently previewed block is the chat website's live page. */
  const selectedBlockIsLive = computed(() => {
    const site = chatWebsite.value
    if (!site?.isPublished) return false
    const c = previewStore.content
    const key = c?.type === 'html' ? c.htmlPublishKey : undefined
    if (!key) return false
    const selected = chatHtmlBlocks.value.find(
      (block) =>
        block.messageId === key.messageId &&
        block.blockIndex === key.blockIndex,
    )
    return Boolean(
      selected?.websiteId === site.websiteId && selected?.isPublished,
    )
  })

  return {
    htmlContent,
    publishPath,
    publishedHtmlApiUrl,
    resolvedHtmlPublish,
    websiteId,
    websiteSlug,
    websiteName,
    websiteDescription,
    websiteOwnedSlug,
    isWebsitePublished,
    effectiveWebsiteId,
    chatHtmlBlocks,
    chatWebsite,
    chatWebsiteSummary,
    selectedBlockIsLive,
  }
}
